/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {LabelValueRow} from './LabelValueRow';
import {SpreaderMeasurement} from './SpreaderGranularInfo';

export interface SpreaderLiquidTotalsProps {
  prewetTotal?: SpreaderMeasurement;
  directTotal?: SpreaderMeasurement;
  dustTotal?: SpreaderMeasurement;
}

const useStyles = makeStyles(() => ({
  container : {
    boxSizing       : 'border-box',
    display         : 'flex',
    width           : '100%',
    flexDirection   : 'column',
    gap             : '16px',
    padding         : '16px',
    backgroundColor : dlsColor('white'),
    border          : `0.5px solid ${dlsColor('grey7')}`,
    borderRadius    : '8px'
  },
  rows : {
    display       : 'flex',
    width         : '100%',
    flexDirection : 'column',
    gap           : '8px'
  },
  header : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey2')
  }
}));

/**
 * SpreaderLiquidTotals renders the Spreader Type 1 Liquid Totals block - a
 * "LIQUID TOTALS" header followed by Prewet/Direct/Dust total rows, each
 * with its own unit suffix (e.g. "gal") via `LabelValueRow`. Each row is
 * only rendered when its field is defined; the whole block (including the
 * header) renders nothing when none of the three fields are provided.
 *
 * Per the Figma mockup, a field whose value is legitimately "N/A" or "0"
 * still renders that value - callers only omit a row (by leaving the
 * corresponding field `undefined`) when the measurement is truly absent.
 *
 * ```typescript
 *
 *   import {SpreaderLiquidTotals} from
 *     '@ion/dls/src/components/assetdetails/current/SpreaderLiquidTotals';
 *   ...
 *
 *   <SpreaderLiquidTotals prewetTotal={{value: '780', unit: 'gal'}}
 *     directTotal={{value: '0', unit: 'gal'}}
 *     dustTotal={{value: 'N/A'}} />
 *
 * ```
 *
 */
export const SpreaderLiquidTotals = ({
  prewetTotal,
  directTotal,
  dustTotal
}: SpreaderLiquidTotalsProps) => {
  const classes = useStyles();
  let block: React.ReactElement = null;

  if (prewetTotal !== undefined || directTotal !== undefined || dustTotal !== undefined) {
    block = (
      <div className={classes.container}>
        <span className={classes.header}>
          Liquid Totals
        </span>
        <div className={classes.rows}>
          {prewetTotal !== undefined && (
            <LabelValueRow label="Prewet Total"
              value={prewetTotal.value}
              unit={prewetTotal.unit} />
          )}
          {directTotal !== undefined && (
            <LabelValueRow label="Direct Total"
              value={directTotal.value}
              unit={directTotal.unit} />
          )}
          {dustTotal !== undefined && (
            <LabelValueRow label="Dust Total"
              value={dustTotal.value}
              unit={dustTotal.unit} />
          )}
        </div>
      </div>
    );
  }

  return block;
};

export default memo(SpreaderLiquidTotals);
